import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import type { DoubleShift } from '../types/Shift'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

export const diggingPatternMutations = {
  async insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    diggerId: number,
    location: DiggingLocationDTO,
  ) {
    if (navigator.onLine) return diggingAjax.insertDiggingLocation(shiftDate, dayOrNight, diggerId, location)
    const tempId = uuidv4()
    // Note: the negative temp id is stored as entityId so it is sent to the server as the
    // pattern's DiggingPatternId. The server maps it to the real id and repoints any loads
    // queued against it. The same id is returned here so the optimistic location (and the
    // loads added to it) reference the value the server will remap.
    const tempDiggingPatternId = -(Date.now())
    await enqueue({
      entity: 'diggingPattern',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempDiggingPatternId,
      data: {
        diggingId: diggerId,
        patternBlastId: location.patternBlastId,
        startTime: location.startTime.toISO(),
        blastMasterId: location.blastMasterId,
      },
    })
    return {
      success: true,
      data: new DiggingLocationDTO({ ...location, diggingPatternId: tempDiggingPatternId }) as DiggingLocationDTO | null,
    }
  },

  async updateDiggingLocation(location: DiggingLocationDTO) {
    if (navigator.onLine) return diggingAjax.updateDiggingLocation(location)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: location.diggingPatternId,
      rowVersion: location.rowVersion,
      data: {
        diggingId: undefined,
        patternBlastId: location.patternBlastId,
        startTime: location.startTime.toISO(),
        blastMasterId: location.blastMasterId,
      },
    })
    return { success: true, data: location as DiggingLocationDTO | null }
  },

  async deleteDiggingPattern(diggingPatternId: number, rowVersion?: string) {
    if (navigator.onLine) return diggingAjax.deleteDiggingPattern(diggingPatternId)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: diggingPatternId,
      rowVersion,
    })
    return { success: true, data: null as null }
  },
}
